import React from 'react';
import { useNavigate } from 'react-router';

import Box from '@mui/material/Box';
import List from '@mui/material/List';
import { Typography } from '@mui/material';
import MuiListItem from '@mui/material/ListItem';
import CheckIcon from '@mui/icons-material/Check';

import { paths } from 'src/routes/paths';
import { useParams, usePathname, useSearchParams } from 'src/routes/hooks';

import { useLoadContract } from 'src/hooks/contracts';
import { useLoadProcurerPreferences } from 'src/hooks/procurer-preferences';

import { capitalizeWords } from 'src/utils/capitalize-words';

import { useContractSetupStore } from 'src/store/contractSetupStore';
import SaveSuccessModal from 'src/pages/procurer/preferences/SaveSuccessModal';
import { usePreferencesStore, type PreferencesStep } from 'src/store/preferencesStore';

import { Header } from 'src/components/Header';
import { StripedCircle } from 'src/components/icons/StripedCircle';

import { useSetupNavigation } from '../../hooks/useSetupNavigation';

type ListItemProps = {
  preferencesStep: PreferencesStep;
  label?: string;
};

const ListItem = ({ preferencesStep, label }: ListItemProps) => {
  const pathname = usePathname();
  const { completedSteps, mode } = usePreferencesStore();
  const { navigateToStep } = useSetupNavigation(mode);
  const isActive = pathname.includes(preferencesStep);
  const [searchParams] = useSearchParams();
  const isEdit = searchParams.get('edit') === 'true';
  const isCompleted = isEdit || completedSteps.includes(preferencesStep);

  return (
    <MuiListItem
      onClick={() => (isCompleted ? navigateToStep(preferencesStep) : null)}
      sx={{ cursor: 'pointer', mb: 3, py: 0 }}
    >
      {isActive ? null : isCompleted ? <CheckIcon color="inherit" /> : <StripedCircle />}
      <Typography
        component="a"
        variant={isActive ? 'h3' : 'h5'}
        textTransform="capitalize"
        ml={1}
        sx={{
          opacity: isCompleted || isActive ? 1 : 0.5,
        }}
      >
        {label ?? preferencesStep}
      </Typography>
    </MuiListItem>
  );
};

interface SetupLayoutProps {
  children: React.ReactNode;
  mode: 'preferences' | 'contract';
}

export const SetupLayout = ({ children, mode }: SetupLayoutProps) => {
  const { contractId } = useParams();
  const contractMetadataStore = useContractSetupStore((s) => s.contractMetadata);
  const { openSaveSuccessModal, setOpenSaveSuccessModal } = usePreferencesStore();
  useLoadProcurerPreferences({ enabled: mode === 'preferences' });
  const isContract = mode === 'contract';
  useLoadContract({
    contractId: contractId as string,
    enabled: isContract && !!contractId,
  });
  const navigate = useNavigate();

  const isExistingContract = isContract && contractMetadataStore.isNew === 'no';
  const isFinalised = isContract && contractMetadataStore.rrule;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header
        sx={(t) => ({
          backgroundColor: t.palette.background.clay,
          borderBottom: '3px solid #00000020',
        })}
      />
      <Box sx={{ display: 'flex', flex: 1 }}>
        <Box
          sx={(theme) => ({
            width: '20%',
            minWidth: 250,
            maxWidth: 300,
            backgroundColor: theme.palette.background.clay,
            color: 'textPrimary',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            px: 3,
            pt: 3,
            pb: 6,
          })}
        >
          <List disablePadding>
            {isContract && <ListItem preferencesStep="start" />}
            <ListItem preferencesStep="geography" />
            <ListItem preferencesStep="themes" />
            <ListItem preferencesStep="impact" />
            <ListItem preferencesStep="scoring" />
            {isExistingContract
              ? [
                  <ListItem preferencesStep="commitments" key="commitments" />,
                  <ListItem preferencesStep="requirements" key="requirements" />,
                  // #634: last, because the cut-off must align to the reporting cadence the
                  // requirements step sets.
                  <ListItem
                    preferencesStep="previous-delivery"
                    label="Previous Delivery"
                    key="previous-delivery"
                  />,
                ]
              : [
                  <ListItem preferencesStep="qualitative" key="qualitative" />,
                  // isOpenForTender flips to false once the procurer awards a supplier's
                  // application (contract/model/update.ts: awardContractAndRejectOthers) -
                  // that's the "awarded" gate. isFinalised (rrule set) is the "finalised" gate.
                  ...(!isContract || contractMetadataStore.isOpenForTender
                    ? []
                    : [
                        <ListItem preferencesStep="requirements" key="requirements" />,
                        ...(isFinalised
                          ? [<ListItem preferencesStep="commitments" key="commitments" />]
                          : []),
                      ]),
                  <ListItem preferencesStep="finalizing" key="finalizing" />,
                ]}
          </List>
        </Box>
        <Box
          sx={(theme) => ({
            flex: 1,
            minWidth: 0,
            backgroundColor: theme.palette.background.chalk,
            pt: 3,
            pb: 8,
            pl: 5,
            pr: 4,
          })}
        >
          {children}
        </Box>
      </Box>
      <SaveSuccessModal
        open={openSaveSuccessModal}
        onClose={() => setOpenSaveSuccessModal(false)}
        title={`${capitalizeWords(mode)} Updated Successfully`}
        primaryCtaLabel={isContract ? 'Go back to contract' : undefined}
        description={`You have successfully updated your ${isContract ? `${capitalizeWords(mode)}.` : 'preferences. These will be applied to all future Social Value contracts you set up.'} `}
        onPrimaryCta={() => {
          setOpenSaveSuccessModal(false);
          navigate(
            isContract && contractId
              ? paths.dashboard.getSingleContractPath(contractId)
              : paths.dashboard.contracts
          );
        }}
        onSecondaryCta={() => setOpenSaveSuccessModal(false)}
      />
    </Box>
  );
};
